import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button';
import '../../styles/pricing-page.css';

const tiers = [
  { name: 'Starter', price: 129, users: '1–9 users' },
  { name: 'Growth', price: 249, users: '10–24 users', popular: true },
  { name: 'Professional', price: 449, users: '25–49 users' },
  { name: 'Business', price: 749, users: '50–99 users' },
  { name: 'Enterprise', price: 1249, users: '100–199 users' },
];

const includedFeatures = [
  'AI-powered report generation',
  'Real-time guard monitoring',
  'Mobile app for guards',
  'Web dashboard for admins',
  'Incident reporting & photos',
  'Multi-language translation',
  'Daily Activity Reports (DARs)',
  'Site & task management',
  'Analytics & insights',
  'Client report sharing',
  'Unlimited reports',
  'Email & SMS notifications',
];

const faqs = [
  {
    question: 'Can I change plans later?',
    answer: 'Yes, upgrade or downgrade anytime from your dashboard.',
  },
  {
    question: 'Is there a contract?',
    answer: 'No contracts. Cancel anytime.',
  },
  {
    question: 'Do you offer custom plans?',
    answer: 'Absolutely! Reach out to us and we\'ll build a plan tailored to your organization.',
  },
];

// Set to true when Stripe is configured and ready to accept payments
const BILLING_ENABLED = false;

const PricingPage: React.FC = () => {
  const [selectedTier, setSelectedTier] = useState('Growth');
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (!BILLING_ENABLED) return;
    navigate(`/signup/${selectedTier.toLowerCase()}`);
  };

  return (
    <div className="pricing-page">
      {/* Hero */}
      <section className="pricing-hero">
        <div className="container">
          <div className="pricing-hero__content fade-in-up">
            <h1 className="pricing-hero__headline">
              Simple, Transparent Pricing
            </h1>
            <p className="pricing-hero__sub">
              Every plan includes full access to every feature.
              Just pick your team size.
            </p>
          </div>
        </div>
      </section>

      {/* Main pricing section — two columns */}
      <section className="pricing-main">
        <div className="container">
          <div className="pricing-main__layout fade-in-up">

            {/* Left: Features */}
            <div className="pricing-main__features">
              <h2 className="pricing-main__features-heading">Every Plan Includes</h2>
              <ul className="pricing-main__features-list">
                {includedFeatures.map((feature) => (
                  <li key={feature} className="pricing-main__feature-item">
                    <span className="pricing-main__check">{'\u2713'}</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Plan selector */}
            <div className="pricing-main__selector">
              <h2 className="pricing-main__selector-heading">Choose Your Plan</h2>
              <div className="pricing-main__options">
                {tiers.map((tier) => (
                  <label
                    key={tier.name}
                    className={`pricing-option${selectedTier === tier.name ? ' pricing-option--selected' : ''}${tier.popular ? ' pricing-option--popular' : ''}`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value={tier.name}
                      checked={selectedTier === tier.name}
                      onChange={() => setSelectedTier(tier.name)}
                      className="pricing-option__radio"
                    />
                    <span className="pricing-option__indicator" />
                    <div className="pricing-option__info">
                      <span className="pricing-option__name">
                        {tier.name}
                        {tier.popular && <span className="pricing-option__badge">Popular</span>}
                      </span>
                      <span className="pricing-option__users">{tier.users}</span>
                    </div>
                    <span className="pricing-option__price">
                      ${tier.price.toLocaleString()}<span className="pricing-option__period">/mo</span>
                    </span>
                  </label>
                ))}
              </div>

              <Button
                variant="primary"
                size="lg"
                onClick={handleGetStarted}
                disabled={!BILLING_ENABLED}
                style={!BILLING_ENABLED ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                {BILLING_ENABLED ? `Get Started with ${selectedTier}` : 'Coming Soon'}
              </Button>

              <p className="pricing-main__custom">
                Need 200+ users? <a href="/contact">Contact us</a> for custom pricing.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pricing-faq">
        <div className="container">
          <h2 className="pricing-faq__heading fade-in-up">
            Frequently Asked Questions
          </h2>
          <div className="pricing-faq__grid fade-in-up-delay">
            {faqs.map((faq) => (
              <div key={faq.question} className="pricing-faq__item">
                <h4 className="pricing-faq__question">{faq.question}</h4>
                <p className="pricing-faq__answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pricing-cta">
        <div className="container">
          <div className="pricing-cta__inner fade-in-up">
            <h2 className="pricing-cta__heading">Need a Custom Solution?</h2>
            <p className="pricing-cta__sub">
              Contact our team to discuss a plan tailored to your organization's
              unique requirements.
            </p>
            <div className="pricing-cta__buttons">
              <Button variant="primary" size="lg" href="/contact">
                Contact Our Team
              </Button>
              <Button variant="secondary" size="lg" href="/features">
                Explore Features
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
